import { useState, useRef, useCallback, useEffect } from 'react';
import MainFormContainer from '@/wrappers/MainFormContainer';
import { useRouter } from '@tanstack/react-router';
import { useDebounce } from '@/hooks/useDebounce';
import { useClaimUpsert } from '@/services/useClaimUpsert';
import Button from '@/ui/Button';
import PostcodeElement, { type PostcodeElementRef } from '@/elements/Postcode';
import { updateBasicInfo } from '@/utils/client-storage/localStorage';
import { DISCORD_BOTS, SUB2_EVENTS } from '@/services/constants';
import {
  getClaimNumber,
  getUserAnswers,
  triggerDiscordMessage,
  triggerSub2DataLayer,
} from '@/utils/functions';
import secureApi from '@/services/secureApi';
import { ChevronRight, Check } from 'lucide-react';
import { GET_JOURNEY_PAGES } from '@/routes/journey.routes/index';

export default function AddressPage() {
  const router = useRouter();
  const pathname = router.state.location?.pathname;
  const { mutate: updateClaim, isPending: isUpdating } = useClaimUpsert();

  useEffect(() => {
    const pc = getUserAnswers()?.basicInfo?.postcode;
    if (!pc) {
      router.navigate({ to: '/' });
    }
  }, [router]);

  const postcodeRef = useRef<PostcodeElementRef | null>(null);
  const [addressSearch, setAddressSearch] = useState('');
  const debouncedSearch = useDebounce(addressSearch, 200);
  const [selectedAddressInternal, setSelectedAddressInternal] = useState<any>(
    getUserAnswers()?.basicInfo?.selectedAddressObject ||
      getUserAnswers()?.basicInfo?.address ||
      null
  );
  const [, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const locationState = router.state.location.state as any;
  const [addresses, setAddresses] = useState<any[]>(
    locationState?.prefetchedAddresses || getUserAnswers()?.basicInfo?.prefetchedAddresses || []
  );

  const [addressError, setAddressError] = useState(false);

  const handleLoadingChange = useCallback((loading: boolean) => {
    setIsSearching(loading);
  }, []);

  const handleAddressesChange = useCallback((newAddresses: any[]) => {
    setAddresses(newAddresses);
  }, []);

  // We need to initialize with the postcode from session/state if available
  const existingPostcode = getUserAnswers()?.basicInfo?.postcode || '';

  const filteredAddresses = debouncedSearch.trim()
    ? addresses.filter((a: any) =>
        (a.label || '').toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : addresses;

  const handleSubmit = async () => {
    if (!selectedAddressInternal) {
      setAddressError(true);
      return;
    }
    setAddressError(false);

    setLoading(true);
    const addressData = selectedAddressInternal.value;
    const rawRes = addressData?.rawResponse || null;

    const payload: any = {
      postcode: existingPostcode.trim().toUpperCase(),
      'redundant-claim-concent': true,
      ...addressData,
      address: addressData,
      manualPreviousAddresses: [],
    };

    secureApi('/save-data8-selection', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        claim_number: getClaimNumber(),
        address: rawRes,
      }),
    });

    updateClaim(payload, {
      onSuccess: () => {
        secureApi(`/claims/${getClaimNumber()}/dm-data-check`, {
          method: 'POST',
        })
          .then((res) => res.json())
          .then((data) => {
            if (data?.responseObject?.dm_sent !== undefined) {
              updateBasicInfo({ dm_sent: data.responseObject.dm_sent });
            }
          })
          .catch((error) => {
            console.error('Error cross-referencing DM data:', error);
          });

        updateBasicInfo(payload);
        triggerSub2DataLayer(SUB2_EVENTS.POSTCODE);

        const info = getUserAnswers()?.basicInfo;
        triggerDiscordMessage({
          content: `# Claim Number: **${getClaimNumber()}**
                - Entry Point: **${pathname}**
                - Address: **${JSON.stringify(info?.address?.address)}**`,
          bot: DISCORD_BOTS.GET_STATED,
        });

        const destination = 'INFO';
        const pages = GET_JOURNEY_PAGES();

        if (pages && pages[destination]) {
          router.navigate({
            to: pages[destination].path,
          });
        } else {
          console.error('Destination page not found:', destination);
          setLoading(false);
        }
      },
      onError: (error) => {
        console.error('Error updating claim:', error);
        setLoading(false);
      },
    });
  };

  return (
    <MainFormContainer
      progress={(2 / 5) * 100}
      currentStep={2}
      totalSteps={5}
      variant="mobile-b"
      isLoading={isSearching}
      contentClassName="w-full"
      aboveFooterContent={
        filteredAddresses.length > 0 &&
        !isSearching && (
          <div className="flex items-center gap-1.5 text-micro font-bold text-primary">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            Click any address above to select it
          </div>
        )
      }
      footer={
        <Button
          onClick={handleSubmit}
          disabled={isUpdating}
          block
          size="large"
          className="py-4"
          pulsating={!!selectedAddressInternal}
          icon={<ChevronRight className="w-6 h-6" />}
        >
          {isUpdating ? 'Processing...' : 'Continue'}
        </Button>
      }
    >
      <div className="flex flex-col gap-1 mb-3">
        <h2 className="text-title font-black text-text-main leading-tight">Pick your address.</h2>
        <p className="text-body text-text-muted leading-snug mt-2">
          We found <span className="text-primary font-semibold">{addresses.length}</span> addresses
          for <span className="font-semibold text-text-main">{existingPostcode.toUpperCase()}</span>
          {addresses.length > 0 && addresses[0].label.split(',').length >= 2 && (
            <>
              ,{' '}
              <span className="font-semibold text-text-main">
                {addresses[0].label.split(',').slice(-2, -1)[0].trim()}
              </span>
            </>
          )}
        </p>
      </div>

      <div className="relative mb-2">
        <input
          type="text"
          placeholder="Start typing your address"
          value={addressSearch}
          onChange={(e) => setAddressSearch(e.target.value)}
          className="w-full p-4 text-body font-bold border-2 border-border-main rounded-xl bg-white focus:outline-none focus:border-primary"
        />
      </div>

      {/* Hidden element to handle background lookup */}
      <div className="hidden">
        <PostcodeElement
          ref={postcodeRef}
          homepageMode={false}
          triggerQueryParams={!addresses.length}
          allowLocalStorageFallback={true}
          onLoadingChange={handleLoadingChange}
          onAddressesChange={handleAddressesChange}
        />
      </div>
      {addressError && (
        <div className="mb-4 text-micro text-red-500 leading-snug font-medium">
          Please select an address from the list to continue.
        </div>
      )}

      {!isSearching && (
        <div className="flex items-center justify-between mb-2 px-1 whitespace-nowrap overflow-hidden">
          <span className="text-[clamp(12px,4.5vw,16px)] font-black text-text-main">
            Select your address
          </span>
          {filteredAddresses.length > 0 && (
            <span className="text-[clamp(10px,3vw,13px)] font-medium text-text-muted pl-2">
              Scroll to see more
            </span>
          )}
        </div>
      )}

      <AddressList
        addresses={filteredAddresses}
        isLoading={isSearching}
        addressSearch={debouncedSearch}
        selectedAddress={selectedAddressInternal}
        onSelect={(addr: any) => {
          setSelectedAddressInternal(addr);
          updateBasicInfo({
            address: addr.value,
            selectedAddressObject: addr,
          });
          setAddressError(false);
        }}
      />
    </MainFormContainer>
  );
}

function AddressList({ addresses, isLoading, addressSearch, selectedAddress, onSelect }: any) {
  if (isLoading && addresses.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="w-full h-[58px] bg-white rounded-xl animate-pulse flex items-center px-4"
          >
            <div className="h-4 bg-gray-100 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!isLoading && addresses.length === 0) {
    return (
      <section className="flex flex-col items-center justify-center p-6 border-2 border-border-main rounded-xl bg-white text-center mb-4">
        <svg
          className="w-10 h-10 text-text-muted/30 mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <div className="text-body font-bold text-text-main leading-tight mb-1">
          We couldn&rsquo;t find a match
        </div>
        <p className="text-body font-medium text-text-muted">
          {addressSearch.trim()
            ? `No results for "${addressSearch}"`
            : 'Try searching with a different term'}
        </p>
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-2 max-h-[380px] md:max-h-[600px] overflow-y-scroll pr-1.5 mb-4 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary [&::-webkit-scrollbar-thumb]:rounded-full">
      {addresses.map((addr: any, i: number) => {
        const isSelected =
          selectedAddress?.value?.addressID === addr.value?.addressID &&
          selectedAddress?.label === addr.label;

        // UI only: remove the city and postcode (usually the last two parts after commas)
        const parts = addr.label.split(',');
        const labelSimplified =
          parts.length > 2
            ? parts.slice(0, -2).join(',').trim()
            : parts.length > 1
              ? parts[0].trim()
              : addr.label;

        return (
          <button
            key={i}
            onClick={() => onSelect(addr)}
            className={`w-full p-3 text-left text-body leading-relaxed rounded-xl transition-all flex items-center gap-3 border-[1.5px] ${
              isSelected
                ? 'bg-bg-medium shadow-sm border-primary text-primary'
                : 'text-text-main hover:bg-black/5 border-primary/20'
            }`}
          >
            <div
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${
                isSelected ? 'bg-primary' : 'bg-gray-300'
              }`}
            />
            <span className="flex-1 font-semibold">{labelSimplified}</span>
            <Check
              className={`w-5 h-5 text-primary flex-shrink-0 transition-opacity ${
                isSelected ? 'opacity-100' : 'opacity-0'
              }`}
              strokeWidth={3}
            />
          </button>
        );
      })}
    </div>
  );
}
